import React, { useState, useRef, useEffect, useCallback } from "react";
import File from "@mui/icons-material/InsertDriveFileOutlined";
import X from "@mui/icons-material/Close";
import Copy from "@mui/icons-material/ContentCopy";
import Check from "@mui/icons-material/Check";
import Mic from "@mui/icons-material/Mic";
import MicOff from "@mui/icons-material/MicOff";
import Send from "@mui/icons-material/Send";
import User from "@mui/icons-material/Person";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { clsx, type ClassValue } from "clsx";
import { useNavigate } from "react-router-dom";
import './RealtimeVoiceBot.less';
import ReactMarkdown from "react-markdown";



// --- Start of Types ---
export type GroundingFile = {
    id: string;
    name: string;
    content: string;
};

export type SessionUpdateCommand = {
    type: "session.update";
    session: {
        type: "realtime";
        instructions?: string;
        metadata?: Record<string, string>;
        audio?: {
            input?: {
                turn_detection?: {
                    type: "server_vad" | "none";
                };
                transcription?: {
                    model: "whisper-1";
                };
            };
        };
    };
};

export type ConversationItemCreateCommand = {
    type: "conversation.item.create";
    item: {
        type: "message";
        role: "system" | "user" | "assistant";
        content: {
            type: "input_text";
            text: string;
        }[];
    };
};

export type ConversationContentItem = {
    type: string;
    text?: string;
    transcript?: string;
};

export type ConversationItemEvent = {
    id: string;
    type: string;
    role: "system" | "user" | "assistant";
    content?: ConversationContentItem[];
};

export type ConversationItemCreateEvent = {
    type: "conversation.item.create";
    item: ConversationItemEvent;
};

export type ResponseCreateCommand = {
    type: "response.create";
    response: {
        conversation?: string | { id: string };
        instructions?: string;
        output_modalities?: ("text" | "audio")[];
    };
};

export type InputAudioBufferAppendCommand = {
    type: "input_audio_buffer.append";
    audio: string;
};

export type InputAudioBufferClearCommand = {
    type: "input_audio_buffer.clear";
};

export type Message = {
    type: string;
};

export type ResponseAudioDelta = {
    type: "response.audio.delta" | "response.output_audio.delta";
    delta: string;
};

export type ResponseAudioTranscriptDelta = {
    type: "response.audio_transcript.delta" | "response.output_audio_transcript.delta";
    delta: string;
};

export type ResponseInputAudioTranscriptionCompleted = {
    type:
        | "response.input_audio_transcription.completed"
        | "conversation.item.input_audio_transcription.completed"
        | "conversation.item.audio_transcription.completed"
        | "input_audio_transcription.completed"
        | "response.input_audio_transcript.completed"
        | "conversation.item.input_audio_transcript.completed"
        | "input_audio_transcript.completed";
    event_id: string;
    item_id: string;
    content_index: number;
    transcript: string;
};

export type ResponseInputAudioTranscriptionDelta = {
    type:
        | "response.input_audio_transcription.delta"
        | "conversation.item.input_audio_transcription.delta"
        | "conversation.item.audio_transcription.delta"
        | "input_audio_transcription.delta"
        | "response.input_audio_transcript.delta"
        | "conversation.item.input_audio_transcript.delta"
        | "input_audio_transcript.delta";
    event_id: string;
    item_id: string;
    content_index: number;
    transcript?: string;
    delta?: string;
};

export type ResponseDone = {
    type: "response.done";
    event_id: string;
    response: {
        id: string;
        output: { id: string; content?: { transcript: string; type: string }[] }[];
    };
};

export type ExtensionMiddleTierToolResponse = {
    type: "extension.middle_tier_tool.response";
    previous_item_id: string;
    tool_name: string;
    tool_result: string; // JSON string that needs to be parsed into ToolResult
};

export type ToolResult = {
    sources: { chunk_id: string; title: string; chunk: string }[];
};

export type ChatSessionMessage = {
    type: "chat_session";
    session_id: string;
};

export type ChatMessage = {
    sender: "user" | "ai";
    text: string;
};

type StoreChatMessagePayload = {
    sender: "user" | "assistant";
    message: string;
    refineQuestion?: string;
};
// --- End of Types ---

// --- Start of lib/utils.ts ---
export function cn(...inputs: ClassValue[]) {
    return clsx(...inputs);
}
// --- End of lib/utils.ts ---

// --- Start of i18n/config.ts ---
const enTranslation = {
    "app": {
        "title": "REALTIME CUSTOM VOICEBOT",
        "footer": "Trained on your Personal Data",
        "stopRecording": "Stop MIC",
        "startRecording": "Start MIC",
        "stopConversation": "Stop conversation"
    },
    "status": {
        "notRecordingMessage": "Tell me about leadership principles...",
        "conversationInProgress": "Conversation in progress"
    },
    "history": {
        "answerHistory": "Answer history",
        "noHistory": "No history yet."
    },
    "groundingFiles": {
        "title": "Grounding files",
        "description": "Files used to ground the answers."
    }
};

function translate(key: string) {
    const value = key.split(".").reduce<unknown>((current, part) => {
        if (current && typeof current === "object" && part in current) {
            return (current as Record<string, unknown>)[part];
        }

        return undefined;
    }, enTranslation);

    return typeof value === "string" ? value : key;
}

function useTranslation() {
    return { t: translate };
}

// --- End of i18n/config.ts ---


// --- Start of components/audio/player.ts ---
export class Player {
    private playbackNode: AudioWorkletNode | null = null;
    private audioContext: AudioContext | null = null;

    async init(sampleRate: number) {
        this.audioContext = new AudioContext({ sampleRate });
        if (this.audioContext.state === "suspended") {
            await this.audioContext.resume();
        }
        // In a real app, the worklet would be in the public folder
        const workletCode = `
            class AudioPlaybackWorklet extends AudioWorkletProcessor {
                constructor() {
                    super();
                    this.port.onmessage = this.handleMessage.bind(this);
                    this.buffer = [];
                }

                handleMessage(event) {
                    if (event.data === null) {
                        this.buffer = [];
                        return;
                    }
                    this.buffer.push(...event.data);
                }

                process(inputs, outputs, parameters) {
                    const output = outputs[0];
                    const channel = output[0];

                    const available = this.buffer.length;
                    const required = channel.length;
                    const toProcessCount = Math.min(available, required);

                    if (toProcessCount > 0) {
                        const toProcess = this.buffer.splice(0, toProcessCount);
                        
                        const float32Array = new Float32Array(toProcess.length);
                        for(let i = 0; i < toProcess.length; i++) {
                            float32Array[i] = toProcess[i] / 32768.0;
                        }
                        channel.set(float32Array);
                    }

                    if (toProcessCount < required) {
                        channel.fill(0, toProcessCount);
                    }

                    return true;
                }
            }
            registerProcessor("audio-playback-worklet", AudioPlaybackWorklet);
        `;
        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        await this.audioContext.audioWorklet.addModule(url);


        this.playbackNode = new AudioWorkletNode(this.audioContext, "audio-playback-worklet");
        this.playbackNode.connect(this.audioContext.destination);
    }

    play(buffer: Int16Array) {
        if (this.playbackNode) {
            this.playbackNode.port.postMessage(buffer);
        }
    }

    stop() {
        if (this.playbackNode) {
            this.playbackNode.port.postMessage(null);
        }
    }
}
// --- End of components/audio/player.ts ---

// --- Start of components/audio/recorder.ts ---
export class Recorder {
    onDataAvailable: (buffer: Float32Array) => void;
    private audioContext: AudioContext | null = null;
    private mediaStream: MediaStream | null = null;
    private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
    private workletNode: AudioWorkletNode | null = null;

    public constructor(onDataAvailable: (buffer: Float32Array) => void) {
        this.onDataAvailable = onDataAvailable;
    }

    async start(stream: MediaStream) {
        try {
            if (this.audioContext) {
                await this.audioContext.close();
            }

            this.audioContext = new AudioContext({ sampleRate: 24000 });

            const workletCode = `
                class AudioProcessorWorklet extends AudioWorkletProcessor {
                    process(inputs, outputs, parameters) {
                        const input = inputs[0];
                        if (input.length > 0) {
                            const channelData = input[0];
                            this.port.postMessage({ buffer: channelData });
                        }
                        return true;
                    }
                }
                registerProcessor('audio-processor-worklet', AudioProcessorWorklet);
            `;
            const blob = new Blob([workletCode], { type: 'application/javascript' });
            const url = URL.createObjectURL(blob);
            await this.audioContext.audioWorklet.addModule(url);


            this.mediaStream = stream;
            this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.mediaStream);

            this.workletNode = new AudioWorkletNode(this.audioContext, "audio-processor-worklet");
            this.workletNode.port.onmessage = event => {
                this.onDataAvailable(event.data.buffer);
            };

            this.mediaStreamSource.connect(this.workletNode);
            this.workletNode.connect(this.audioContext.destination);
        } catch (error) {
            this.stop();
        }
    }

    async stop() {
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        if (this.audioContext) {
            await this.audioContext.close();
            this.audioContext = null;
        }

        this.mediaStreamSource = null;
        this.workletNode = null;
    }
}
// --- End of components/audio/recorder.ts ---

// --- Start of hooks/useAudioPlayer.tsx ---
const SAMPLE_RATE = 24000;

function useAudioPlayer() {
    const audioPlayer = useRef<Player>();

    const reset = () => {
        audioPlayer.current = new Player();
        audioPlayer.current.init(SAMPLE_RATE);
    };

    const play = (base64Audio: string) => {
        const binary = atob(base64Audio);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        const pcmData = new Int16Array(bytes.buffer);

        audioPlayer.current?.play(pcmData);
    };

    const stop = () => {
        audioPlayer.current?.stop();
    };

    return { reset, play, stop };
}
// --- End of hooks/useAudioPlayer.tsx ---

// --- Start of hooks/useAudioRecorder.tsx ---
const BUFFER_SIZE = 4800;

type UseAudioRecorderParameters = {
    onAudioRecorded: (base64: string) => void;
};

function useAudioRecorder({ onAudioRecorded }: UseAudioRecorderParameters) {
    const audioRecorder = useRef<Recorder>();

    let buffer = new Uint8Array();

    const appendToBuffer = (newData: Uint8Array) => {
        const newBuffer = new Uint8Array(buffer.length + newData.length);
        newBuffer.set(buffer);
        newBuffer.set(newData, buffer.length);
        buffer = newBuffer;
    };

    const handleAudioData = (data: Float32Array) => {
        // Convert Float32Array to Int16Array (16-bit PCM)
        const pcmData = new Int16Array(data.length);
        for (let i = 0; i < data.length; i++) {
            const s = Math.max(-1, Math.min(1, data[i]));
            pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        const uint8Array = new Uint8Array(pcmData.buffer);
        appendToBuffer(uint8Array);

        if (buffer.length >= BUFFER_SIZE) {
            const toSend = new Uint8Array(buffer.slice(0, BUFFER_SIZE));
            buffer = new Uint8Array(buffer.slice(BUFFER_SIZE));

            // Efficiently convert Uint8Array to base64
            let binary = "";
            for (let i = 0; i < toSend.length; i++) {
                binary += String.fromCharCode(toSend[i]);
            }
            const base64 = btoa(binary);

            onAudioRecorded(base64);
        }
    };

    const start = async () => {
        if (!audioRecorder.current) {
            audioRecorder.current = new Recorder(handleAudioData);
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioRecorder.current.start(stream);
    };

    const stop = async () => {
        await audioRecorder.current?.stop();
    };

    return { start, stop };
}
// --- End of hooks/useAudioRecorder.tsx ---

// --- Start of hooks/useRealtime.tsx ---
type UseRealTimeParameters = {
    useDirectAoaiApi?: boolean;
    aoaiEndpointOverride?: string;
    aoaiApiKeyOverride?: string;
    aoaiModelOverride?: string;
    token?: string | null;
    chatSessionId?: string | null;
    userId?: string | null;
    userEmail?: string | null;

    enableInputAudioTranscription?: boolean;
    onWebSocketOpen?: () => void;
    onWebSocketClose?: () => void;
    onWebSocketError?: (event: Event) => void;
    onWebSocketMessage?: (event: MessageEvent<any>) => void;
    onReceivedChatSession?: (message: ChatSessionMessage) => void;

    onReceivedConversationItemCreate?: (message: ConversationItemCreateEvent) => void;
    onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
    onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
    onReceivedResponseDone?: (message: ResponseDone) => void;
    onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
    onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
    onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
    onReceivedInputAudioTranscriptionDelta?: (message: ResponseInputAudioTranscriptionDelta) => void;
    onReceivedError?: (message: Message) => void;
};

type RealtimeWebSocketOptions = {
    onOpen?: () => void;
    onClose?: (event: CloseEvent) => void;
    onError?: (event: Event) => void;
    onMessage?: (event: MessageEvent<any>) => void;
    shouldReconnect?: () => boolean;
};

function useRealtimeWebSocket(url: string, options: RealtimeWebSocketOptions) {
    const socketRef = useRef<WebSocket | null>(null);
    const optionsRef = useRef(options);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const messageQueueRef = useRef<string[]>([]);

    optionsRef.current = options;

    useEffect(() => {
        if (!url) {
            messageQueueRef.current = [];
            socketRef.current?.close();
            socketRef.current = null;
            return;
        }

        let closedByEffect = false;

        const connect = () => {
            try {
                const socket = new WebSocket(url);
                socketRef.current = socket;

                socket.onopen = () => {
                    optionsRef.current.onOpen?.();
                    const queuedMessages = messageQueueRef.current.splice(0);
                    queuedMessages.forEach(message => socket.send(message));
                };
                socket.onclose = event => {
                    optionsRef.current.onClose?.(event);
                    if (!closedByEffect && (optionsRef.current.shouldReconnect?.() ?? true)) {
                        reconnectTimeoutRef.current = setTimeout(connect, 1000);
                    }
                };
                socket.onerror = event => optionsRef.current.onError?.(event);
                socket.onmessage = event => optionsRef.current.onMessage?.(event);
            } catch (error) {
                console.error("Failed to open realtime WebSocket:", error);
            }
        };

        connect();

        return () => {
            closedByEffect = true;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            socketRef.current?.close();
            socketRef.current = null;
        };
    }, [url]);

    const sendJsonMessage = useCallback((message: unknown) => {
        if (!url) {
            console.warn("Realtime WebSocket is not ready. Message was not sent.");
            return;
        }

        const payload = JSON.stringify(message);
        const socket = socketRef.current;

        if (socket?.readyState === WebSocket.OPEN) {
            socket.send(payload);
            return;
        }

        messageQueueRef.current.push(payload);
    }, [url]);

    return { sendJsonMessage };
}

function useRealTime({
    useDirectAoaiApi,
    aoaiEndpointOverride,
    aoaiApiKeyOverride,
    aoaiModelOverride,
    token,
    chatSessionId,
    userId,
    userEmail,
    enableInputAudioTranscription,
    onWebSocketOpen,
    onWebSocketClose,
    onWebSocketError,
    onWebSocketMessage,
    onReceivedChatSession,
    onReceivedResponseDone,
    onReceivedConversationItemCreate,
    onReceivedResponseAudioDelta,
    onReceivedResponseAudioTranscriptDelta,
    onReceivedInputAudioBufferSpeechStarted,
    onReceivedExtensionMiddleTierToolResponse,
    onReceivedInputAudioTranscriptionCompleted,
    onReceivedInputAudioTranscriptionDelta,
    onReceivedError
}: UseRealTimeParameters) {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
    const wsBaseUrl = apiBaseUrl.replace(/^http/, "ws");
    const realtimeToken = token?.trim() ?? "";
    const realtimeUser = userEmail?.trim() ?? "";
    const wsEndpoint = wsBaseUrl && realtimeToken && realtimeUser
        ? `${wsBaseUrl}ws/realtime/?token=${encodeURIComponent(realtimeToken)}&user=${encodeURIComponent(realtimeUser)}`
        : "";

    const { sendJsonMessage: originalSendJsonMessage } = useRealtimeWebSocket(wsEndpoint, {
        onOpen: () => onWebSocketOpen?.(),
        onClose: () => onWebSocketClose?.(),
        onError: event => onWebSocketError?.(event),
        onMessage: event => onMessageReceived(event),
        shouldReconnect: () => true
    });

    const isCustomRealtimeBackend = Boolean(wsEndpoint) && (wsEndpoint.startsWith(wsBaseUrl) || wsEndpoint.includes("/realtime"));
    const supportsInputResponseCommands = Boolean(isCustomRealtimeBackend);
    const supportsSessionMetadata = Boolean(isCustomRealtimeBackend);

    const sendJsonMessage = (message: any) => {
        originalSendJsonMessage({ ...message });
    };

    const updateLatestUserQuestionMetadata = (question: string) => {
        if (!supportsSessionMetadata) {
            return;
        }
        const trimmed = question.trim();
        if (!trimmed) {
            return;
        }
    };

    const startSession = () => {
        const command: SessionUpdateCommand = {
            type: "session.update",
            session: {
                type: "realtime",
                audio: {
                    input: {
                        turn_detection: {
                            type: "server_vad"
                        }
                    }
                }
            }
        };

        if (enableInputAudioTranscription) {
            command.session.audio!.input!.transcription = {
                model: "whisper-1"
            };
        }

        sendJsonMessage(command);

        const systemMessage = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "system",
                content: [
                    {
                        type: "input_text",
                        text: "Marshall Goldsmith Development and leadership coach"
                    }
                ]
            }
        };
        sendJsonMessage(systemMessage);
    };

    const addUserAudio = (base64Audio: string) => {
        const command: InputAudioBufferAppendCommand = {
            type: "input_audio_buffer.append",
            audio: base64Audio
        };

        sendJsonMessage(command);
    };

    const inputAudioBufferClear = () => {
        const command: InputAudioBufferClearCommand = {
            type: "input_audio_buffer.clear"
        };

        sendJsonMessage(command);
    };

    const sendInputResponseToBackend = (responseText: string, userQuestion: string) => {
        if (!supportsInputResponseCommands) {
            return;
        }
        const trimmedQuestion = userQuestion?.trim();
        const trimmedResponse = responseText?.trim();
        if (!trimmedQuestion && !trimmedResponse) {
            return;
        }
        const command = {
            type: "input_response",
            response: trimmedResponse,
            question: trimmedQuestion
        };
        sendJsonMessage(command);
    };

    const onMessageReceived = (event: MessageEvent<any>) => {
        onWebSocketMessage?.(event);

        let message: Message;
        try {
            message = JSON.parse(event.data);
        } catch (e) {
            console.error("Failed to parse JSON message:", e);
            throw e;
        }

        if (typeof message.type === "string") {
            const typeValue = message.type;
            const isDeltaEvent =
                typeValue.includes("input_audio_transcription.delta") ||
                typeValue.includes("input_audio_transcript.delta") ||
                typeValue === "conversation.item.audio_transcription.delta";
            const isCompletedEvent =
                typeValue.includes("input_audio_transcription.completed") ||
                typeValue.includes("input_audio_transcript.completed") ||
                typeValue === "conversation.item.audio_transcription.completed";

            if (isDeltaEvent) {
        onReceivedInputAudioTranscriptionDelta?.(message as ResponseInputAudioTranscriptionDelta);
        return;
            }
            if (isCompletedEvent) {
        onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
        return;
            }
        }

        switch (message.type) {
            case "chat_session":
                onReceivedChatSession?.(message as ChatSessionMessage);
                break;
            case "response.done":
                onReceivedResponseDone?.(message as ResponseDone);
                break;
            case "response.audio.delta":
            case "response.output_audio.delta":
                onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
                break;
            case "response.audio_transcript.delta":
            case "response.output_audio_transcript.delta":
                onReceivedResponseAudioTranscriptDelta?.(message as ResponseAudioTranscriptDelta);
                break;
            case "input_audio_buffer.speech_started":
                onReceivedInputAudioBufferSpeechStarted?.(message);
                break;
            case "conversation.item.create":
            case "conversation.item.added":
                onReceivedConversationItemCreate?.(message as ConversationItemCreateEvent);
                break;
            case "extension.middle_tier_tool_response":
                onReceivedExtensionMiddleTierToolResponse?.(message as ExtensionMiddleTierToolResponse);
                break;
            case "error":
                onReceivedError?.(message);
                break;
        }
    };

    const cancelResponse = () => {
        sendJsonMessage({ type: "response.cancel" });
    };

    const sendUserTextQuestion = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) {
            return;
        }

        updateLatestUserQuestionMetadata(trimmed);

        const userMessage: ConversationItemCreateCommand = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: trimmed
                    }
                ]
            }
        };
        sendJsonMessage(userMessage);

        const responseCommand: ResponseCreateCommand = {
            type: "response.create",
            response: {
                output_modalities: ["audio"]
            }
        };
        sendJsonMessage(responseCommand);
    };

    return { startSession, addUserAudio, inputAudioBufferClear, cancelResponse, sendUserTextQuestion, sendInputResponseToBackend, updateLatestUserQuestionMetadata };
}
// --- End of hooks/useRealtime.tsx ---

// --- Start of UI Components ---

// components/ui/button.tsx
type MotionDivProps = React.HTMLAttributes<HTMLDivElement> & {
    initial?: unknown;
    animate?: unknown;
    exit?: unknown;
    transition?: unknown;
    variants?: unknown;
    custom?: unknown;
    onLayoutAnimationStart?: () => void;
    onLayoutAnimationComplete?: () => void;
};

const MotionDiv = React.forwardRef<HTMLDivElement, MotionDivProps>(({
    initial,
    animate,
    exit,
    transition,
    variants,
    custom,
    onLayoutAnimationStart,
    onLayoutAnimationComplete,
    ...props
}, ref) => <div ref={ref} {...props} />);
MotionDiv.displayName = "MotionDiv";

function AnimatePresence({ children }: { children: React.ReactNode }) {
    return <>{children}</>;
}

const motion = { div: MotionDiv };

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
type ButtonSize = "default" | "sm" | "lg" | "icon";

const buttonBaseClasses = "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50";
const buttonVariantClasses: Record<ButtonVariant, string> = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline"
};
const buttonSizeClasses: Record<ButtonSize, string> = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
    icon: "h-10 w-10"
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant = "default", size = "default", asChild, ...props }, ref) => {
    return <button className={cn(buttonBaseClasses, buttonVariantClasses[variant], buttonSizeClasses[size], className)} ref={ref} {...props} />;
});
Button.displayName = "Button";

// components/ui/card.tsx
const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)} {...props} />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
));
CardFooter.displayName = "CardFooter";

// components/ui/status-message.tsx
type StatusMessageProperties = {
    isRecording: boolean;
};

function StatusMessage({ isRecording }: StatusMessageProperties) {
    const { t } = useTranslation();
    if (!isRecording) {
        return <p className="text mb-4 mt-6">{t("status.notRecordingMessage")}</p>;
    }

    return (
        <div className="flex items-center">
            <div className="relative h-6 w-6 overflow-hidden">
                <div className="absolute inset-0 flex items-end justify-around">
                    {[...Array(4)].map((_, i) => (
                        <div
                            key={i}
                            className="w-1 rounded-full bg-purple-600 opacity-80"
                            style={{
                                animation: `barHeight${(i % 3) + 1} 1s ease-in-out infinite`,
                                animationDelay: `${i * 0.1}s`
                            }}
                        />
                    ))}
                </div>
            </div>
            <p className="text mb-4 ml-2 mt-6">{t("status.conversationInProgress")}</p>
        </div>
    );
}

// components/ui/grounding-file.tsx
type GroundingFileProps = {
    value: GroundingFile;
    onClick: () => void;
};

function GroundingFileComponent({ value, onClick }: GroundingFileProps) {
    return (
        <Button variant="outline" size="sm" className="rounded-full" onClick={onClick}>
            <File className="mr-2 h-4 w-4" />
            {value.name}
        </Button>
    );
}

// components/ui/grounding-files.tsx
const groundingFileVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: (i: number) => ({
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            delay: i * 0.1,
            duration: 0.3,
            type: "spring",
            stiffness: 300,
            damping: 20
        }
    })
};

type GroundingFilesProps = {
    files: GroundingFile[];
    onSelected: (file: GroundingFile) => void;
};

function GroundingFiles({ files, onSelected }: GroundingFilesProps) {
    const { t } = useTranslation();
    const isAnimating = useRef(false);

    if (files.length === 0) {
        return null;
    }

    return (
        <Card className="m-4 max-w-full md:max-w-md lg:min-w-96 lg:max-w-2xl">
            <CardHeader>
                <CardTitle className="text-xl">{t("groundingFiles.title")}</CardTitle>
                <CardDescription>{t("groundingFiles.description")}</CardDescription>
            </CardHeader>
            <CardContent>
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`h-full ${isAnimating ? "overflow-hidden" : "overflow-y-auto"}`}
                        onLayoutAnimationStart={() => (isAnimating.current = true)}
                        onLayoutAnimationComplete={() => (isAnimating.current = false)}
                    >
                        <div className="flex flex-wrap gap-2">
                            {files.map((file, index) => (
                                <motion.div key={index} variants={groundingFileVariants} initial="hidden" animate="visible" custom={index}>
                                    <GroundingFileComponent key={index} value={file} onClick={() => onSelected(file)} />
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </CardContent>
        </Card>
    );
}

// components/ui/grounding-file-view.tsx
type GroundingFileViewProps = {
    groundingFile: GroundingFile | null;
    onClosed: () => void;
};

function GroundingFileView({ groundingFile, onClosed }: GroundingFileViewProps) {
    return (
        <AnimatePresence>
            {groundingFile && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
                    onClick={() => onClosed()}
                >
                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white p-6"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold">{groundingFile.name}</h2>
                            <Button
                                aria-label="Close grounding file view"
                                variant="ghost"
                                size="sm"
                                className="text-gray-500 hover:text-gray-700"
                                onClick={() => onClosed()}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        <div className="flex-grow overflow-hidden">
                            <pre className="h-[40vh] overflow-auto text-wrap rounded-md bg-gray-100 p-4 text-sm">
                                <code>{groundingFile.content}</code>
                            </pre>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

const SESSION_WAVE_BAR_COUNT = 7;
const LOADING_WAVE_BAR_COUNT = 6;

const AI_AVATAR_SRC = "/static/media/mg-face-crop.cf87795398d89c1bc47f.jpeg";

export default function App() {
    const navigate = useNavigate();
    const [wsToken, setWsToken] = useState<string | null>(null);
    const [chatSessionId, setChatSessionId] = useState<string | null>(null);
    const [canStartSession, setCanStartSession] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [showLoadingScreen, setShowLoadingScreen] = useState(true);
    const [groundingFiles, setGroundingFiles] = useState<GroundingFile[]>([]);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const messagesRef = useRef<ChatMessage[]>(messages);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [textEntry, setTextEntry] = useState("");
    const [waveHeights, setWaveHeights] = useState<number[]>(() => Array.from({ length: SESSION_WAVE_BAR_COUNT }, () => 10));
    const [isVoiceLoading, setIsVoiceLoading] = useState(false);
    const [isUserSpeaking, setIsUserSpeaking] = useState(false);
    const [isMicMuted, setIsMicMuted] = useState(false);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);
    const isUserSpeakingRef = useRef(false);
    const aiResponseTranscriptRef = useRef("");
    const transcriptRef = useRef("");
    const userQuestionRef = useRef("");
    const sessionInitializedRef = useRef(false);
    const startSessionRef = useRef<(() => void) | null>(null);
    const pendingUserMessageIndexRef = useRef<number | null>(null);
    const copyResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const waveAnimationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const voiceLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const persistedResponseEventIdsRef = useRef<Set<string>>(new Set());
    const hasStoredCurrentQuestionRef = useRef(false);
    const realtimeReady = Boolean(wsToken?.trim() && userEmail?.trim());

    const storeChatMessage = async ({ sender, message, refineQuestion }: StoreChatMessagePayload) => {
        const trimmedMessage = (message ?? "").trim();
        // console.log(messages)
        if (!trimmedMessage) {
            return;
        }

        const sessionIdentifier = chatSessionId ?? wsToken ?? userId ?? "local-session";

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/chat/messages/store/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    session_id: sessionIdentifier,
                    sender,
                    message: trimmedMessage,
                    refine_question: refineQuestion?.trim() ?? ""
                })
            });
        } catch (error) {
            console.error("Failed to store chat message:", error);
        }
    };
    const persistUserQuestionIfNeeded = async (questionText: string) => {
        const trimmed = (questionText ?? "").trim();
        if (!trimmed || hasStoredCurrentQuestionRef.current) {
            return;
        }
        hasStoredCurrentQuestionRef.current = true;
        await storeChatMessage({
            sender: "user",
            message: trimmed,
            refineQuestion: trimmed
        });
    };
    const loadingOverlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSpeechStarted = () => {
        stopAudioPlayer();
        isUserSpeakingRef.current = true;
        setIsUserSpeaking(true);
        aiResponseTranscriptRef.current = "";
        userQuestionRef.current = "";
        transcriptRef.current = "";
        hasStoredCurrentQuestionRef.current = false;

        const nextIndex = messagesRef.current.length;
        pendingUserMessageIndexRef.current = nextIndex;
        setMessages(prev => [...prev, { sender: 'user', text: '...' }]);
    };

    const resolvePendingMessageIndex = (messageList: ChatMessage[]) => {
        const currentIndex = pendingUserMessageIndexRef.current;
        if (currentIndex !== null && currentIndex < messageList.length) {
            return currentIndex;
        }

        for (let index = messageList.length - 1; index >= 0; index -= 1) {
            const candidate = messageList[index];
            if (candidate.sender === 'user' && candidate.text === '...') {
                pendingUserMessageIndexRef.current = index;
                return index;
            }
        }

        return null;
    };

    const handleTranscriptionDelta = (partialTranscript: string) => {
        const normalizedTranscript = (partialTranscript ?? "").trim();
        
        // Safety check: only process if it looks like actual text (not binary/audio data)
        if (!normalizedTranscript || /^[\x00-\x1F\x7F-\x9F]*$/.test(normalizedTranscript)) {
            return;
        }
        transcriptRef.current = normalizedTranscript;

        setMessages(prev => {
            const pendingIndex = resolvePendingMessageIndex(prev);
            if (pendingIndex !== null && pendingIndex < prev.length) {
                const newMessages = [...prev];
                newMessages[pendingIndex] = { ...newMessages[pendingIndex], text: normalizedTranscript };
                return newMessages;
            }
            // Don't add new messages here - only update existing placeholder
            return prev;
        });
    };

    const handleTranscriptionCompleted = (finalTranscript: string) => {
        applyUserTranscript(finalTranscript);
    };

    const applyUserTranscript = (transcript: string) => {
        const cleanedTranscript = (transcript ?? "").trim();
        const fallbackTranscript = transcriptRef.current?.trim?.() ?? "";
        const finalTranscript = cleanedTranscript || fallbackTranscript;
        if (!finalTranscript) {
            return false;
        }

        updateLatestUserQuestionMetadata(finalTranscript);

        setMessages(prev => {
            const pendingIndex = pendingUserMessageIndexRef.current;
            if (pendingIndex !== null && pendingIndex < prev.length) {
                const updated = [...prev];
                updated[pendingIndex] = { ...updated[pendingIndex], text: finalTranscript };
                return updated;
            }

            const placeholderIndex = prev.findIndex(msg => msg.sender === 'user' && msg.text === '...');
            if (placeholderIndex !== -1) {
                const updated = [...prev];
                updated[placeholderIndex] = { ...updated[placeholderIndex], text: finalTranscript };
                return updated;
            }

            // Should not reach here if handleSpeechStarted was called properly
            return prev;
        });

        userQuestionRef.current = finalTranscript;
        transcriptRef.current = "";
        pendingUserMessageIndexRef.current = null;
        isUserSpeakingRef.current = false;
        setIsUserSpeaking(false);
        void persistUserQuestionIfNeeded(finalTranscript);
        return true;
    };

    useEffect(() => {
        document.body.style.backgroundColor = 'white';

        return () => {
            document.body.style.backgroundColor = '';
        };
    }, []);

    useEffect(() => {
        return () => {
            if (copyResetTimeout.current) {
                clearTimeout(copyResetTimeout.current);
            }
            if (waveAnimationIntervalRef.current) {
                clearInterval(waveAnimationIntervalRef.current);
                waveAnimationIntervalRef.current = null;
            }
            if (voiceLoadingTimeoutRef.current) {
                clearTimeout(voiceLoadingTimeoutRef.current);
                voiceLoadingTimeoutRef.current = null;
            }
            if (loadingOverlayTimeoutRef.current) {
                clearTimeout(loadingOverlayTimeoutRef.current);
                loadingOverlayTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("wsToken");
        setWsToken(token);
        const sessionId = localStorage.getItem("chat_session_id");
        setChatSessionId(sessionId);
        const storedUserId = localStorage.getItem("user_id");
        setUserId(storedUserId);
        const authenticatedUserString = localStorage.getItem("authenticatedUser");
        if (authenticatedUserString) {
            try {
                const authenticatedUser = JSON.parse(authenticatedUserString);
                if (authenticatedUser && authenticatedUser.email) {
                    setUserEmail(authenticatedUser.email);
                } else {
                    setUserEmail(authenticatedUserString);
                }
            } catch (e) {
                setUserEmail(authenticatedUserString);
            }
        }
    }, []);

    const { startSession, addUserAudio, inputAudioBufferClear, cancelResponse, sendUserTextQuestion, sendInputResponseToBackend, updateLatestUserQuestionMetadata } = useRealTime({
        token: wsToken,
        chatSessionId: chatSessionId,
        userId: userId,
        userEmail: userEmail,
        enableInputAudioTranscription: true,
        onWebSocketOpen: () => {
            if (!sessionInitializedRef.current) {
                startSessionRef.current?.();
                sessionInitializedRef.current = true;
            }
        },
        onWebSocketClose: () => {
            sessionInitializedRef.current = false;
        },
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedChatSession: message => {
            localStorage.setItem("chat_session_id", message.session_id);
            setChatSessionId(message.session_id);
        },
        onReceivedConversationItemCreate: message => {
            // User message is already handled by transcription callbacks
            // No need to apply it again when server confirms
            if (message.item?.role !== 'user') {
                return;
            }
        },
        onReceivedError: message => {
            console.error("OpenAI Realtime error while handling user input:", {
                lastUserQuestion: userQuestionRef.current,
                error: message
            });
        },
        onReceivedResponseDone: message => {
            if (!message.response?.output?.length) {
                return;
            }

            const responseEventId = message.event_id;
            const alreadyPersisted = responseEventId
                ? persistedResponseEventIdsRef.current.has(responseEventId)
                : false;
            const responseTranscript =
                message.response?.output?.[0]?.content?.find(entry => entry.transcript)?.transcript?.trim() || "";
            const aiResponse = responseTranscript || aiResponseTranscriptRef.current.trim();

            if (!userQuestionRef.current.trim()) {
                const fallbackTranscript = transcriptRef.current.trim();
                if (fallbackTranscript) {
                    applyUserTranscript(fallbackTranscript);
                }
            }

            const finalQuestion = userQuestionRef.current.trim() || transcriptRef.current.trim();
            if (finalQuestion) {
                console.log("🎤 FINAL QUESTION:", finalQuestion);
            }
            const finalResponse = aiResponse || responseTranscript || aiResponseTranscriptRef.current.trim();
            
            if (!finalQuestion && !finalResponse) {
                console.warn("OpenAI response.done received but no transcript or question could be inferred", {
                    raw: message
                });
                return;
            }

            sendInputResponseToBackend(finalResponse, finalQuestion);

            if (alreadyPersisted) {
                return;
            }
            if (responseEventId) {
                persistedResponseEventIdsRef.current.add(responseEventId);
            }

            const persistMessagesSequentially = async () => {
                await persistUserQuestionIfNeeded(finalQuestion);

                if (finalResponse) {
                    await storeChatMessage({
                        sender: "assistant",
                        message: finalResponse,
                        refineQuestion: finalQuestion || finalResponse
                    });
                }
            };

            persistMessagesSequentially();
        },
        onReceivedResponseAudioDelta: message => {
            ensureAudioPlayerReady();
            playAudio(message.delta);
        },
        onReceivedInputAudioBufferSpeechStarted: () => handleSpeechStarted(),
        onReceivedInputAudioTranscriptionCompleted: message => {
            const finalTranscript = message.transcript?.trim() || transcriptRef.current.trim();
            if (finalTranscript) {
                handleTranscriptionCompleted(finalTranscript);
            }
        },
        onReceivedInputAudioTranscriptionDelta: message => {
            const partialTranscript = message.delta != null
                ? `${transcriptRef.current}${message.delta}`
                : message.transcript;
            if (partialTranscript && typeof partialTranscript === 'string' && partialTranscript.trim().length > 0) {
                handleTranscriptionDelta(partialTranscript);
            }
        },
        onReceivedResponseAudioTranscriptDelta: message => {
            let handledAsUser = false;
            setMessages(prev => {
                if (isUserSpeakingRef.current) {
                    const index = pendingUserMessageIndexRef.current;
                    if (index != null && index < prev.length) {
                        handledAsUser = true;
                        const updated = [...prev];
                        const existingText = updated[index].text;
                        const newText = existingText === '...' ? message.delta : existingText + message.delta;
                        updated[index] = { ...updated[index], text: newText };
                        transcriptRef.current = newText;
                        return updated;
                    }
                }

                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.sender === 'ai') {
                    const newText = lastMessage.text + message.delta;
                    aiResponseTranscriptRef.current += message.delta;
                    return [...prev.slice(0, -1), { ...lastMessage, text: newText }];
                }

                aiResponseTranscriptRef.current = message.delta;
                return [...prev, { sender: 'ai', text: message.delta }];
            });

            if (isUserSpeakingRef.current && !handledAsUser) {
                isUserSpeakingRef.current = false;
                setIsUserSpeaking(false);
            }
        },
        onReceivedExtensionMiddleTierToolResponse: message => {
            const result: ToolResult = JSON.parse(message.tool_result);

            const files: GroundingFile[] = result.sources.map(x => {
                return { id: x.chunk_id, name: x.title, content: x.chunk };
            });

            setGroundingFiles(prev => [...prev, ...files]);
        }
    });
    startSessionRef.current = startSession;

    const ensureSessionInitialized = () => {
        if (!realtimeReady) {
            console.warn("Realtime session is not ready. Missing websocket token or user email.");
            return false;
        }
        if (sessionInitializedRef.current) {
            return true;
        }
        startSession();
        sessionInitializedRef.current = true;
        return true;
    };

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const audioPlayerReadyRef = useRef(false);
    const ensureAudioPlayerReady = () => {
        if (audioPlayerReadyRef.current) {
            return;
        }
        resetAudioPlayer();
        audioPlayerReadyRef.current = true;
    };
    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({ onAudioRecorded: addUserAudio });

    useEffect(() => {
        const generateWaveHeights = () => Array.from({ length: SESSION_WAVE_BAR_COUNT }, () => 25 + Math.random() * 65);
        const stopWaveAnimation = () => {
            if (waveAnimationIntervalRef.current) {
                clearInterval(waveAnimationIntervalRef.current);
                waveAnimationIntervalRef.current = null;
            }
        };

        const shouldAnimate = isRecording && (isUserSpeaking);

        stopWaveAnimation();

        if (shouldAnimate) {
            setWaveHeights(generateWaveHeights());
            waveAnimationIntervalRef.current = setInterval(() => {
                setWaveHeights(generateWaveHeights());
            }, 160);
        } else {
            const restingHeight = isRecording ? 15 : 5;
            setWaveHeights(Array.from({ length: SESSION_WAVE_BAR_COUNT }, () => restingHeight));
        }

        return () => {
            stopWaveAnimation();
        };
    }, [isRecording, isUserSpeaking]);

    const startVoiceLoadingTimer = () => {
        if (voiceLoadingTimeoutRef.current) {
            clearTimeout(voiceLoadingTimeoutRef.current);
        }
        setIsVoiceLoading(true);
        voiceLoadingTimeoutRef.current = setTimeout(() => {
            setIsVoiceLoading(false);
            voiceLoadingTimeoutRef.current = null;
        }, 5000);
    };

    const triggerLoadingOverlay = (duration = 5000) => {
        setShowLoadingScreen(true);
        if (loadingOverlayTimeoutRef.current) {
            clearTimeout(loadingOverlayTimeoutRef.current);
        }
        loadingOverlayTimeoutRef.current = setTimeout(() => {
            setShowLoadingScreen(false);
            loadingOverlayTimeoutRef.current = null;
        }, duration);
    };

    useEffect(() => {
        setCanStartSession(localStorage.getItem("canAccessQnA"));
        
        const startRecordingOnMount = async () => {
            
            try {
                if (!ensureSessionInitialized()) {
                    return;
                }
                await startAudioRecording();
                resetAudioPlayer();
                audioPlayerReadyRef.current = true;
                setIsRecording(true);
                setIsMicMuted(false);
                startVoiceLoadingTimer();
                
            } catch (error) {
                console.error("Failed to initialize audio recording:", error);
                setShowLoadingScreen(false);
            }
        
        };

        const canAccess = localStorage.getItem("canAccessQnA");
        setCanStartSession(canAccess);

        if (canAccess !== "yes") {
            setShowLoadingScreen(false);
            return;
        }

        if (!realtimeReady) {
            return;
        }

        startRecordingOnMount();
        setShowLoadingScreen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    
    }, [realtimeReady]);

    useEffect(() => {
        if (!messagesEndRef.current) {
            return;
        }
        messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const onToggleListening = async () => {
        setCanStartSession(localStorage.getItem("canAccessQnA"));
        if (localStorage.getItem("canAccessQnA") === "yes") {
        if (!isRecording) {
            if (!ensureSessionInitialized()) {
                setShowLoadingScreen(false);
                return;
            }
            triggerLoadingOverlay();
            await startAudioRecording();
            resetAudioPlayer();
            audioPlayerReadyRef.current = true;
            setIsRecording(true);
            setIsMicMuted(false);
            startVoiceLoadingTimer();
        } else {
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
            setIsMicMuted(false);
            setIsVoiceLoading(false);
            setIsUserSpeaking(false);
            isUserSpeakingRef.current = false;
            audioPlayerReadyRef.current = false;
            sessionInitializedRef.current = false;
            if (voiceLoadingTimeoutRef.current) {
                clearTimeout(voiceLoadingTimeoutRef.current);
                voiceLoadingTimeoutRef.current = null;
            }
        }
    }
    };

    const handleMicToggle = async () => {
        if (!isRecording) {
            return;
        }

        if (isMicMuted) {
            await startAudioRecording();
            setIsMicMuted(false);
        } else {
            await stopAudioRecording();
            inputAudioBufferClear();
            setIsMicMuted(true);
            setIsUserSpeaking(false);
            isUserSpeakingRef.current = false;
        }
    };

    const handleCloseAndNavigate = async () => {
        if (isRecording) {
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
            setIsMicMuted(false);
            setIsVoiceLoading(false);
            setIsUserSpeaking(false);
            isUserSpeakingRef.current = false;
            audioPlayerReadyRef.current = false;
            sessionInitializedRef.current = false;
            if (voiceLoadingTimeoutRef.current) {
                clearTimeout(voiceLoadingTimeoutRef.current);
                voiceLoadingTimeoutRef.current = null;
            }
        }
        
        navigate(`/chat`)
    };

    const handleCopy = (text: string, index: number) => {
        if (!navigator.clipboard) {
            console.warn('Clipboard API not available in this browser.');
            return;
        }

        navigator.clipboard.writeText(text).then(() => {
            setCopiedMessageIndex(index);
            if (copyResetTimeout.current) {
                clearTimeout(copyResetTimeout.current);
            }
            copyResetTimeout.current = setTimeout(() => {
                setCopiedMessageIndex(current => (current === index ? null : current));
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
        });
    };

    const handleTextSubmit = () => {
        const trimmed = textEntry.trim();
        if (!trimmed) {
            return;
        }

        if (!ensureSessionInitialized()) {
            return;
        }
        stopAudioPlayer();
        ensureAudioPlayerReady();
        inputAudioBufferClear();
        cancelResponse();
        handleSpeechStarted();
        handleTranscriptionCompleted(trimmed);
        sendUserTextQuestion(trimmed);
        setTextEntry("");
    };

    const { t } = useTranslation();

    return (
    <>{canStartSession === "yes"? (
        <>
            {showLoadingScreen ? (
                <div className="loading-container">
                    <div className="loading-panel">
                        <motion.div
                            className="loading-orb"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1.15, repeat: Infinity, ease: "linear" }}
                        />
                        <div className="loading-wave" aria-hidden="true">
                            {Array.from({ length: LOADING_WAVE_BAR_COUNT }).map((_, index) => (
                                <span
                                    key={`loading-wave-${index}`}
                                    style={{ animationDelay: `${index * 0.12}s` }}
                                />
                            ))}
                        </div>
                        <p className="loading-text">Initializing session...</p>
                        <p className="loading-subtext">Tuning your realtime leadership coach</p>
                    </div>
                </div>
            ) : (
                <div className="realtime-voice-section">
                    <br /><br />
                    <div className="chat-window">
                        <div className="messages-container">
                            {messages.map((msg, index) => {
                                const isCopied = copiedMessageIndex === index;
                                const isUserMessage = msg.sender === 'user';
                                return (
                                    <div
                                        key={index}
                                        className={clsx(
                                            "message-row",
                                            isUserMessage ? "is-user" : "is-ai"
                                        )}
                                    >
                                        <div
                                            className={clsx(
                                                "message-avatar",
                                                isUserMessage ? "is-user" : "is-ai"
                                            )}
                                        >
                                            {isUserMessage ? (
                                                <User className="message-avatar__icon" aria-hidden="true" />
                                            ) : (
                                                <img
                                                    src={AI_AVATAR_SRC}
                                                    alt="AI avatar"
                                                    className="message-avatar__image"
                                                />
                                            )}
                                        </div>
                                        <div
                                            className={clsx(
                                                "message-bubble",
                                                isUserMessage ? "is-user" : "is-ai"
                                            )}
                                        >
                                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                                            {!isUserMessage && (
                                                <div className="message-actions">
                                                    <button
                                                        className="copy-button"
                                                        onClick={() => handleCopy(msg.text, index)}
                                                        aria-label={isCopied ? "Message copied" : "Copy message"}
                                                    >
                                                        {isCopied ? <Check className="copy-icon" /> : <Copy className="copy-icon" />}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className="input-container">
                            <div className="input-wrapper">
                                <div className={clsx("session-input", { "is-recording": isRecording })}>
                                    <div className="session-input__left">
                                        <input
                                            type="text"
                                            className="session-input__field"
                                            placeholder={isRecording ? "Type" : "Ask anything"}
                                            value={textEntry}
                                            onChange={event => setTextEntry(event.target.value)}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' && !event.shiftKey) {
                                                    event.preventDefault();
                                                    handleTextSubmit();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="session-input__right">
                                        <button
                                            type="button"
                                            className="session-input__send"
                                            onClick={handleTextSubmit}
                                            disabled={!textEntry.trim()}
                                            aria-label="Send text"
                                        >
                                            <Send className="session-input__send-icon" />
                                        </button>
                                        <button
                                            type="button"
                                            className="session-input__mic"
                                            onClick={handleMicToggle}
                                            disabled={!isRecording}
                                            aria-label={
                                                !isRecording
                                                    ? t("app.startRecording")
                                                    : isMicMuted
                                                        ? "Unmute microphone"
                                                        : "Mute microphone"
                                            }
                                        >
                                            {(!isRecording || isMicMuted) ? (
                                                <MicOff className="session-input__mic-icon" />
                                            ) : (
                                                <Mic className="session-input__mic-icon" />
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={clsx("session-input__voice", { "is-recording": isRecording })}
                                            onClick={onToggleListening}
                                            aria-label={isRecording ? t("app.stopRecording") : t("app.startRecording")}
                                        >
                                            {isRecording ? (
                                                <span
                                                    className={clsx("session-input__voice-wave", {
                                                        "is-active": isUserSpeaking
                                                    })}
                                                    aria-label={"Listening for speech"}
                                                >
                                                    {waveHeights.map((height, waveIndex) => (
                                                        <span
                                                            key={`input-wave-${waveIndex}`}
                                                            className="session-input__voice-wave-bar"
                                                            style={{ height: `${height}%` }}
                                                        />
                                                    ))}
                                                </span>
                                            ) : (
                                                <GraphicEqIcon className="session-input__voice-icon" />
                                            )}
                                            {isRecording && (
                                                <span className="session-input__voice-label">End</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontWeight: "bold",
            color: "#144126",
          }}
        >
          {canStartSession === null ? "Loading realtime voice bot..." : "You do not have access to this feature."}
        </div>
      )}
    </>
    
    );
}
